<template>
  <div class="bg-light rounded-3 p-4 p-md-5 mb-3">
    <!-- Title -->
    <h2 class="h4 mb-5 text-center">
      <!--      <i class="fi-info-circle text-primary fs-5 mt-n1 me-2"></i>-->
      Please provide a few details
    </h2>
    <!-- / Title -->

    <!-- Job Title -->
    <div class="mb-5">
      <label class="form-label" for="jobTitle">Job Title</label>
      <input v-model="job_title" type="text" class="form-control"
             placeholder="Firstly pop in a title, for example 'Advice on retrospective PR219 return'"
             id="jobTitle"/>
    </div>
    <!-- / Job Title -->

    <!-- Description -->
    <div class="mb-5">
      <label class="form-label" for="jobDescription">Job Description</label>
      <textarea v-model="job_description"
                placeholder="Now put a bit more detail to help your professionals with the context, including whether fixed or variable fees are preferred"
                class="form-control" id="jobDescription" rows="5"></textarea>
    </div>
    <!-- / Description -->

    <!-- Should Invite Client -->
    <div v-if="!isClient" class="mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div class="form-check form-check-inline">
          <input v-model="should_invite_client"
                 value="personal"
                 class="form-check-input" id="shouldInviteClient" type="checkbox" name="shouldInviteClient">
          <label class="form-check-label" for="shouldInviteClient">Invite the client at this stage?</label>
        </div>
        
        <!-- Sage Integration Button -->
        <button v-if="sageIntegrationStatus === 'connected' && shouldInviteClient" 
                @click="openSageContactModal"
                type="button"
                class="btn btn-outline-primary btn-sm"
                :disabled="isLoadingSageContacts">
          <spinner v-if="isLoadingSageContacts" class="spinner-border-sm me-2"></spinner>
          <i v-else class="fi-users me-2"></i>
          Select Contact In Sage
        </button>
      </div>
    </div>
    <!-- / Should Invite Client -->

    <!-- Client Details -->
    <div v-if="!isClient && shouldInviteClient">
      <!-- Client Name -->
      <div class="mb-5">
        <label class="form-label" for="clientName">Client Name</label>
        <input v-model="client_name" type="text" class="form-control"
               placeholder="Next put in the name of your client"
               id="clientName"/>
      </div>
      <!-- / Client Name -->

      <!-- Client Email -->
      <div class="">
        <label class="form-label" for="clientEmail">Client Email</label>
        <input v-model="client_email" type="email"
               placeholder="And lastly add your client's email address"
               class="form-control" id="clientEmail"/>
      </div>
      <!-- / Client Email -->
    </div>
    <!-- / Client Details -->

    <!-- Footer Navigation -->
    <div class="wizard-footer-navigation">
      <!-- Previous -->
      <a class="btn btn-primary btn-lg rounded-pill me-3" href="#"
         @click.prevent="handlePreviousButton">
        <i class="fi-chevron-left fs-sm me-2"></i>
        Previous
      </a>
      <!-- / Previous -->

      <!-- Next -->
      <a class="btn btn-primary btn-lg rounded-pill ms-sm-auto" href="#"
         @click.prevent="handleNextButton" :class="{'disabled': !this.canGoForward}">
        Next
        <i class="fi-chevron-right fs-sm ms-2"></i>
      </a>
      <!-- / Next -->
    </div>
    <!-- / Footer Navigation -->
  </div>

  <!-- Sage Contact Selection Modal -->
  <div v-if="showSageContactModal" class="modal fade show" style="display: block;" tabindex="-1">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fi-users me-2"></i>
            Select Contact from Sage
          </h5>
          <button type="button" class="btn-close" @click="closeSageContactModal"></button>
        </div>
        <div class="modal-body">
          <!-- Search Form -->
          <div class="row mb-4">
            <div class="col-md-4">
              <label for="sageContactSearch" class="form-label">Search Contacts</label>
              <input 
                type="text" 
                id="sageContactSearch"
                class="form-control" 
                v-model="sageContactSearch" 
                placeholder="Search by name..."
                @keyup.enter="searchSageContacts">
            </div>
            <div class="col-md-3">
              <label for="sageContactType" class="form-label">Contact Type</label>
              <select 
                id="sageContactType"
                class="form-select" 
                v-model="sageContactTypeFilter">
                <option value="">All Types</option>
                <option value="CUSTOMER">Customer</option>
                <option value="VENDOR">Vendor</option>
              </select>
            </div>
            <div class="col-md-3">
              <label for="sageItemsPerPage" class="form-label">Items per Page</label>
              <select 
                id="sageItemsPerPage"
                class="form-select" 
                v-model="sageItemsPerPage">
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
            <div class="col-md-2 d-flex align-items-end">
              <button 
                class="btn btn-primary w-100" 
                @click="searchSageContacts"
                :disabled="isLoadingSageContacts">
                <spinner v-if="isLoadingSageContacts" class="spinner-border-sm me-2"></spinner>
                <i v-else class="fi-search me-2"></i>
                Search
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="isLoadingSageContacts && !sageContacts.length" class="text-center py-5">
            <spinner></spinner>
            <p class="mt-2 text-muted">Loading contacts...</p>
          </div>

          <!-- No Results -->
          <div v-else-if="!isLoadingSageContacts && !sageContacts.length && hasSearchedSage" class="text-center py-5">
            <i class="fi-users text-muted" style="font-size: 3rem;"></i>
            <p class="mt-2 text-muted">No contacts found matching your criteria.</p>
          </div>

          <!-- Contacts Table -->
          <div v-else-if="sageContacts.length" class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="contact in sageContacts" :key="contact.id">
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="avatar avatar-xs bg-primary bg-opacity-10 text-primary rounded-circle me-2">
                        {{ getInitials(contact.displayed_as) }}
                      </div>
                      <div>
                        <div class="fw-medium">{{ contact.displayed_as }}</div>
                        <small class="text-muted">ID: {{ contact.id }}</small>
                      </div>
                    </div>
                  </td>
                  <td class="align-middle">
                    <span v-if="contact.email">{{ contact.email }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="align-middle">
                    <span v-if="contact.telephone">{{ contact.telephone }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <button 
                      class="btn btn-sm btn-outline-primary"
                      @click="selectSageContact(contact)"
                      :disabled="!contact.email">
                      Select
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Pagination -->
            <div v-if="sageContactsPagination" class="d-flex justify-content-between align-items-center mt-4">
              <div class="text-muted">
                Showing {{ ((sageContactsPagination.$page - 1) * sageContactsPagination.$itemsPerPage) + 1 }} 
                to {{ Math.min(sageContactsPagination.$page * sageContactsPagination.$itemsPerPage, sageContactsPagination.$total) }} 
                of {{ sageContactsPagination.$total }} contacts
              </div>
              <nav>
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: sageContactsPagination.$page <= 1 }">
                    <button class="page-link" @click="goToSagePage(sageContactsPagination.$page - 1)" :disabled="sageContactsPagination.$page <= 1">
                      <i class="fi-chevron-left"></i>
                    </button>
                  </li>
                  <li v-for="page in visibleSagePages" :key="page" class="page-item" :class="{ active: page === sageContactsPagination.$page }">
                    <button class="page-link" @click="goToSagePage(page)">{{ page }}</button>
                  </li>
                  <li class="page-item" :class="{ disabled: !sageContactsPagination.$next }">
                    <button class="page-link" @click="goToSagePage(sageContactsPagination.$page + 1)" :disabled="!sageContactsPagination.$next">
                      <i class="fi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          <!-- Initial State -->
          <div v-else class="text-center py-5">
            <i class="fi-search text-muted" style="font-size: 3rem;"></i>
            <p class="mt-2 text-muted">Enter search criteria and click "Search" to find contacts.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeSageContactModal">Close</button>
        </div>
      </div>
    </div>
  </div>
  <!-- / Sage Contact Selection Modal -->

  <!-- Modal Backdrop -->
  <div v-if="showSageContactModal" class="modal-backdrop fade show"></div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import sageApi from "../../../../api/sage";
import Spinner from "../../../../components/Spinner.vue";
import { useToast } from 'vue-toastification';

const toast = useToast();

export default {
  name: "ProvideJobDetails",
  
  components: {
    Spinner
  },

  data() {
    return {
      // Sage Integration
      sageIntegrationStatus: 'checking', // checking, connected, disconnected, error
      showSageContactModal: false,
      isLoadingSageContacts: false,
      sageContacts: [],
      sageContactsPagination: null,
      hasSearchedSage: false,
      
      // Sage Search Filters
      sageContactSearch: '',
      sageContactTypeFilter: 'CUSTOMER',
      sageItemsPerPage: 20,
      sageCurrentPage: 1
    }
  },

  computed: {
    ...mapGetters('findALawyer', {
      jobTitle: 'jobTitle',
      jobDescription: 'jobDescription',
      shouldInviteClient: 'shouldInviteClient',
      clientName: 'clientName',
      clientEmail: 'clientEmail'
    }),

    ...mapGetters('auth', {
      isClient: 'isClient'
    }),

    canGoForward() {
      let canGoForward = (this.jobTitle && this.jobTitle.trim())
          && (this.jobDescription && this.jobDescription.trim());

      if (this.shouldInviteClient) {
        canGoForward = canGoForward
            && (this.clientName && this.clientName.trim())
            && (this.clientEmail && this.clientEmail.trim());
      }

      return canGoForward;
    },

    job_title: {
      get() {
        return this.jobTitle;
      },
      set(val) {
        this.setJobTitle(val);
      }
    },

    job_description: {
      get() {
        return this.jobDescription;
      },
      set(val) {
        this.setJobDescription(val);
      }
    },

    should_invite_client: {
      get() {
        return this.shouldInviteClient;
      },
      set(val) {
        this.setShouldInviteClient(val);
      }
    },

    client_name: {
      get() {
        return this.clientName;
      },
      set(val) {
        this.setClientName(val);
      }
    },

    client_email: {
      get() {
        return this.clientEmail;
      },
      set(val) {
        this.setClientEmail(val);
      }
    },

    visibleSagePages() {
      if (!this.sageContactsPagination) return [];
      
      const totalPages = Math.ceil(this.sageContactsPagination.$total / this.sageContactsPagination.$itemsPerPage);
      const currentPage = this.sageContactsPagination.$page;
      const pages = [];
      
      // Show up to 5 pages around current page
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  created() {
    if (this.isClient) {
      this.setShouldInviteClient(false);
    }
    
    // Check Sage integration status
    this.checkSageIntegration();
  },
  methods: {
    ...mapActions('findALawyer', {
      decrementWizardStage: 'decrementWizardStage',
      incrementWizardStage: 'incrementWizardStage',
      setJobTitle: 'setJobTitle',
      setJobDescription: 'setJobDescription',
      setShouldInviteClient: 'setShouldInviteClient',
      setClientName: 'setClientName',
      setClientEmail: 'setClientEmail'
    }),

    handlePreviousButton() {
      this.decrementWizardStage();
    },

    handleNextButton() {
      if (!this.canGoForward) {
        return false;
      }
      this.incrementWizardStage();
    },

    // Sage Integration Methods
    async checkSageIntegration() {
      try {
        const response = await sageApi.checkIntegration();
        
        if (response.data.has_integration) {
          this.sageIntegrationStatus = 'connected';
        } else {
          this.sageIntegrationStatus = 'disconnected';
        }
      } catch (error) {
        console.error('Failed to check Sage integration:', error);
        this.sageIntegrationStatus = 'error';
      }
    },

    openSageContactModal() {
      this.showSageContactModal = true;
      this.resetSageSearch();
    },

    closeSageContactModal() {
      this.showSageContactModal = false;
      this.resetSageSearch();
    },

    resetSageSearch() {
      this.sageContacts = [];
      this.sageContactsPagination = null;
      this.hasSearchedSage = false;
      this.sageContactSearch = '';
      this.sageContactTypeFilter = 'CUSTOMER';
      this.sageItemsPerPage = 20;
      this.sageCurrentPage = 1;
    },

    async searchSageContacts(page = 1) {
      this.isLoadingSageContacts = true;
      this.sageCurrentPage = page;

      try {
        const params = {
          items_per_page: parseInt(this.sageItemsPerPage),
          page: parseInt(page) || 1
        };

        // Add search term if provided
        if (this.sageContactSearch.trim()) {
          params.search = this.sageContactSearch.trim();
        }

        // Add contact type filter if selected
        if (this.sageContactTypeFilter) {
          params.contact_type_id = this.sageContactTypeFilter;
        }

        const response = await sageApi.getContacts(params);

        this.sageContacts = response.data.$items || [];
        this.sageContactsPagination = {
          $total: response.data.$total || 0,
          $page: response.data.$page || 1,
          $next: response.data.$next || null,
          $itemsPerPage: response.data.$itemsPerPage || parseInt(this.sageItemsPerPage)
        };
        this.hasSearchedSage = true;

      } catch (error) {
        console.error('Failed to fetch Sage contacts:', error);
        toast.error('Failed to fetch contacts. Please try again.');
        this.sageContacts = [];
        this.sageContactsPagination = null;
        this.hasSearchedSage = true;
      } finally {
        this.isLoadingSageContacts = false;
      }
    },

    goToSagePage(page) {
      if (page < 1 || !this.sageContactsPagination) return;
      
      const totalPages = Math.ceil(this.sageContactsPagination.$total / this.sageContactsPagination.$itemsPerPage);
      if (page > totalPages) return;

      this.searchSageContacts(page);
    },

    selectSageContact(contact) {
      if (!contact.email) {
        toast.warning('This contact does not have an email address.');
        return;
      }

      // Set client name and email
      this.setClientName(contact.displayed_as || contact.name);
      this.setClientEmail(contact.email);
      
      // Close the modal
      this.closeSageContactModal();
      
      toast.success(`Selected contact: ${contact.displayed_as}`);
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
  },
}
</script>

<style scoped>

</style>